import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { DEPARTMENTS, formatErr } from "../lib/constants";
import PersonnelDialog from "../components/PersonnelDialog";

export default function Personnel() {
  const [hotels, setHotels] = useState([]);
  const [activeHotel, setActiveHotel] = useState(null);
  const [activeDept, setActiveDept] = useState("mutfak");
  const [personnel, setPersonnel] = useState([]);
  const [dialog, setDialog] = useState({ open: false, person: null });

  const loadHotels = () => api.get("/hotels").then((r) => {
    setHotels(r.data);
    setActiveHotel((cur) => cur || r.data[0]?.code);
  });

  const loadPersonnel = () => {
    if (!activeHotel) return;
    api.get("/personnel", { params: { hotel_code: activeHotel, department_code: activeDept } })
       .then((r) => setPersonnel(r.data));
  };

  useEffect(() => { loadHotels(); }, []);
  useEffect(loadPersonnel, [activeHotel, activeDept]);

  const refresh = () => { loadPersonnel(); loadHotels(); };

  const remove = async (p) => {
    if (!window.confirm(`#${p.sira_no} ${p.full_name} personel listesinden çıkarılsın mı?`)) return;
    try { await api.delete(`/personnel/${p.id}`); toast.success("Personel silindi"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h1 className="font-display text-xl font-bold text-cyan-300 tracking-widest uppercase text-glow-sm">
          Personel Yönetimi
        </h1>
        <button data-testid="add-personnel-btn" onClick={() => setDialog({ open: true, person: null })}
          className="btn-cyan px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest flex items-center gap-2">
          <Plus className="w-4 h-4" /> Personel Ekle
        </button>
      </div>

      <div data-testid="hotel-tabs" className="flex gap-2 flex-wrap mb-4 border-b border-cyan-500/20 pb-2">
        {hotels.map((h) => (
          <button
            key={h.code}
            data-testid={`hotel-tab-${h.code}`}
            onClick={() => setActiveHotel(h.code)}
            className={`px-4 py-2 rounded-t-lg font-mono text-xs uppercase tracking-wider transition ${
              activeHotel === h.code
                ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/40 border-b-transparent"
                : "text-slate-400 hover:text-cyan-300"
            }`}
          >
            {h.name} <span className="ml-1 opacity-60">({h.personnel_count})</span>
          </button>
        ))}
      </div>

      <div data-testid="dept-tabs" className="flex gap-2 flex-wrap mb-5">
        {DEPARTMENTS.map((d) => (
          <button
            key={d.code}
            data-testid={`dept-tab-${d.code}`}
            onClick={() => setActiveDept(d.code)}
            className={`px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-wider border transition ${
              activeDept === d.code
                ? "border-cyan-400 text-cyan-200 bg-cyan-500/15"
                : "border-slate-700 text-slate-400 hover:border-cyan-500/40"
            }`}
          >
            {d.name}
          </button>
        ))}
      </div>

      <div className="glass-card hud-corners p-4">
        <div className="text-xs font-mono text-cyan-300/60 uppercase tracking-widest mb-3">
          {personnel.length} personel — Sıra No ile sıralı
        </div>
        <div className="space-y-1.5">
          {personnel.map((p) => (
            <div key={p.id} data-testid={`person-${p.id}`} className="flex items-center justify-between p-2.5 rounded bg-slate-900/40 border border-cyan-500/10 hover:border-cyan-500/30 transition">
              <div className="flex items-center gap-4">
                <span className="font-mono text-sm font-bold text-cyan-400 w-12">#{p.sira_no}</span>
                <div>
                  <div className="text-sm font-medium text-slate-100">{p.full_name}</div>
                  <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">{p.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`edit-person-${p.sira_no}`} onClick={() => setDialog({ open: true, person: p })} className="p-1.5 rounded border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/15"><Pencil className="w-3.5 h-3.5" /></button>
                <button data-testid={`delete-person-${p.sira_no}`} onClick={() => remove(p)} className="p-1.5 rounded border border-rose-500/30 text-rose-300 hover:bg-rose-500/15"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
          {personnel.length === 0 && (
            <div className="text-center py-10 text-slate-500 font-mono text-sm">Bu departmanda personel bulunamadı</div>
          )}
        </div>
      </div>
      <PersonnelDialog open={dialog.open} person={dialog.person} hotels={hotels}
        defaults={{ hotel_code: activeHotel || "", department_code: activeDept }}
        onClose={() => setDialog({ open: false, person: null })}
        onSaved={() => { setDialog({ open: false, person: null }); refresh(); }} />
    </div>
  );
}
